/**
 * @file TournamentContext/helpers.ts
 * @description Helper functions for TournamentContext payment logic
 * 
 * Task 19.4: Implement isPaidTournament and isPaymentRequired helpers
 * Reference: .taskmaster/docs/task-19-tournament-context-payment.md Section 4.4
 */

import { EntryFee } from '../../types/tournament';
import { PaymentStatus } from '../../types/payment';

/**
 * Determines if a tournament requires payment based on its entry fee configuration.
 * 
 * @param entryFee - The entry fee configuration from tournament details
 * @returns true if the tournament is paid (type === 'PAID'), false otherwise
 */
export const isPaidTournament = (entryFee: EntryFee | null | undefined): boolean => {
  return entryFee?.type === 'PAID';
};

/**
 * Determines if payment is required for a user to join a tournament.
 * 
 * Payment is required when:
 * - Tournament is paid (isPaidTournament returns true)
 * - User has not already joined the tournament
 * - User does not have a confirmed payment
 * 
 * @param entryFee - The entry fee configuration from tournament details
 * @param userPaymentStatus - The user's current payment status for this tournament
 * @param joinedStatus - Whether the user has already joined the tournament
 * @returns true if payment is required, false otherwise
 */
export const isPaymentRequired = (
  entryFee: EntryFee | null | undefined,
  userPaymentStatus: PaymentStatus | null | undefined,
  joinedStatus: boolean | null | undefined
): boolean => {
  // If user already joined, no payment required
  if (joinedStatus === true) {
    return false;
  }
  
  // If tournament is free, no payment required
  if (!isPaidTournament(entryFee)) {
    return false;
  }
  
  // If user has confirmed payment, no payment required
  if (userPaymentStatus === 'CONFIRMED') {
    return false;
  }
  
  // Tournament is paid, user hasn't joined, and no confirmed payment
  return true;
};
